import { useEffect, useMemo, useState } from "react";
import { fetchConsultores, fetchGlobalConsultores, updateConsultor } from "../lib/api";
import { useSnapshot } from "../contexts/SnapshotContext";
import { formatEUR, formatDate, RANK_LABELS } from "../lib/format";
import { RankBadge } from "../components/RankBadge";
import { MagnifyingGlass, PencilSimple, ArrowsClockwise, CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const ALL_RANKS = ["Consultor", "Lider Junior", "Lider Ejecutivo", "Lider Master"];

const RANK_ORDER = { "Consultor": 0, "Lider Junior": 1, "Lider Ejecutivo": 2, "Lider Master": 3 };

function getSortValue(c, col) {
  switch (col) {
    case "id":         return c.id_consultor;
    case "nome":       return (c.nome || "").toLowerCase();
    case "coordenador":return (c.coordenador || "").toLowerCase();
    case "titulo":     return RANK_ORDER[c.titulo] ?? -1;
    case "qualifying": return RANK_ORDER[c.rank_qualifying] ?? -1;
    case "nivel":      return c.nivel ?? 0;
    case "personal":   return c.vendas_pessoais ?? 0;
    case "equipo":     return c.vendas_equipa ?? 0;
    case "comision":   return c.commission_calc?.total ?? 0;
    case "pedido":     return c.ultimo_pedido_data ?? "";
    default:           return 0;
  }
}

export default function Consultoras() {
  const { selected, isGlobal, selectedPais } = useSnapshot();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [rankFilter, setRankFilter] = useState("all");
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [sortCol, setSortCol] = useState("personal");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    const r = isGlobal
      ? await fetchGlobalConsultores(selectedPais ? { pais: selectedPais } : {})
      : await fetchConsultores({ snapshot_id: selected?.id || undefined });
    setItems(r.items || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [selected?.id, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return items.filter((c) => {
      if (rankFilter !== "all" && c.rank_qualifying !== rankFilter && c.titulo !== rankFilter)
        return false;
      if (activeOnly && !c.active) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !c.nome.toLowerCase().includes(q) &&
          !String(c.id_consultor).includes(q) &&
          !(c.lider_subequipa || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [items, search, rankFilter, activeOnly]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = getSortValue(a, sortCol);
      const vb = getSortValue(b, sortCol);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      vendas_pessoais: c.vendas_pessoais,
      vendas_equipa: c.vendas_equipa,
      vendas_comissoes: c.vendas_comissoes,
      diretos_ativos: c.diretos_ativos,
      junior_lideres_equipa: c.junior_lideres_equipa,
      lideres_executivos_equipa: c.lideres_executivos_equipa,
      titulo: c.titulo,
    });
  };

  const saveEdit = async () => {
    try {
      // Only send fields that actually changed to avoid clobbering existing values
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== editing[k]) payload[k] = v;
      });
      if (Object.keys(payload).length === 0) {
        toast.info("Sin cambios");
        setEditing(null);
        return;
      }
      await updateConsultor(editing.id_consultor, payload);
      toast.success(`Datos de ${editing.nome} actualizados`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Error al guardar los cambios");
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="consultoras-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
            Equipo
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
            Consultoras
          </h1>
          <p className="mt-1 text-[#736F68] text-sm">
            {sorted.length} de {items.length} mostradas · pulsa el lápiz para ajustar manualmente.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-[#E5E0D8] hover:bg-[#F2EFEA] text-[#2D2A26] transition-colors self-start"
          data-testid="refresh-consultores-btn"
        >
          <ArrowsClockwise size={14} />
          Refrescar
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#736F68]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, ID o líder…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C57B67]/30 focus:border-[#C57B67]"
            data-testid="consultoras-search"
          />
        </div>
        <select
          value={rankFilter}
          onChange={(e) => setRankFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm"
          data-testid="rank-filter"
        >
          <option value="all">Todos los estatus</option>
          {ALL_RANKS.map((r) => (
            <option key={r} value={r}>
              {RANK_LABELS[r]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-[#2D2A26] px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white cursor-pointer">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="accent-[#C57B67]"
            data-testid="active-only-checkbox"
          />
          Solo activas
        </label>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#736F68]">
                <Th col="id" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>ID</Th>
                <Th col="nome" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Nombre</Th>
                {isGlobal && <Th col="coordenador" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Coordinadora</Th>}
                <Th col="titulo" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Estatus</Th>
                <Th col="qualifying" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Calificable</Th>
                <Th col="nivel" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Nivel</Th>
                <Th col="personal" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right>Personal</Th>
                <Th col="equipo" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right>Equipo</Th>
                <Th col="comision" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right>Comisión calc.</Th>
                <Th col="pedido" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>Último pedido</Th>
                {!isGlobal && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F2EFEA]">
              {loading && (
                <tr>
                  <td colSpan={isGlobal ? 10 : 10} className="px-4 py-8 text-center text-[#736F68]">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={isGlobal ? 10 : 10} className="px-4 py-8 text-center text-[#736F68]">
                    No hay resultados.
                  </td>
                </tr>
              )}
              {sorted.map((c) => (
                <tr
                  key={`${c.coordenador}-${c.id_consultor}`}
                  className="hover:bg-[#F9F8F6] transition-colors"
                  data-testid={`consultora-row-${c.id_consultor}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-[#736F68]">{c.id_consultor}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#2D2A26]">{c.nome}</p>
                    {c.lider_subequipa && c.lider_subequipa !== c.nome && (
                      <p className="text-[11px] text-[#736F68] mt-0.5">
                        Líder: {c.lider_subequipa}
                      </p>
                    )}
                    {c.promotion_eligible && (
                      <span className="inline-block mt-1 text-[10px] font-semibold text-[#9C6A2F] bg-[#E3A569]/15 px-1.5 py-0.5 rounded">
                        ↑ Califica para {RANK_LABELS[c.promotion_eligible]}
                      </span>
                    )}
                  </td>
                  {isGlobal && (
                    <td className="px-4 py-3 text-xs text-[#736F68]">
                      {c.coordenador || "—"}
                    </td>
                  )}
                  <td className="px-4 py-3"><RankBadge rank={c.titulo} /></td>
                  <td className="px-4 py-3"><RankBadge rank={c.rank_qualifying} /></td>
                  <td className="px-4 py-3 text-[#736F68]">N{c.nivel}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatEUR(c.vendas_pessoais)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatEUR(c.vendas_equipa)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#8A9A86] font-semibold">
                    {formatEUR(c.commission_calc.total)}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#736F68]">
                    {c.ultimo_pedido_data ? formatDate(c.ultimo_pedido_data) : "—"}
                  </td>
                  {!isGlobal && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1.5 rounded-md hover:bg-[#F2EFEA] text-[#736F68] hover:text-[#C57B67] transition-colors"
                        data-testid={`edit-consultora-${c.id_consultor}`}
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Ajustar manualmente</DialogTitle>
            <p className="text-sm text-[#736F68]">{editing?.nome}</p>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <Field label="Ventas personales (€)">
              <Input
                type="number"
                step="0.01"
                value={form.vendas_pessoais ?? ""}
                onChange={(e) => setForm({ ...form, vendas_pessoais: e.target.value })}
                data-testid="edit-vendas-pessoais"
              />
            </Field>
            <Field label="Ventas equipo (€)">
              <Input
                type="number"
                step="0.01"
                value={form.vendas_equipa ?? ""}
                onChange={(e) => setForm({ ...form, vendas_equipa: e.target.value })}
                data-testid="edit-vendas-equipa"
              />
            </Field>
            <Field label="Comisiones (€)">
              <Input
                type="number"
                step="0.01"
                value={form.vendas_comissoes ?? ""}
                onChange={(e) => setForm({ ...form, vendas_comissoes: e.target.value })}
              />
            </Field>
            <Field label="Estatus">
              <Select value={form.titulo} onValueChange={(v) => setForm({ ...form, titulo: v })}>
                <SelectTrigger data-testid="edit-titulo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALL_RANKS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {RANK_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Directos activos">
              <Input
                type="number"
                value={form.diretos_ativos ?? ""}
                onChange={(e) => setForm({ ...form, diretos_ativos: e.target.value })}
              />
            </Field>
            <Field label="Júnior en equipo">
              <Input
                type="number"
                value={form.junior_lideres_equipa ?? ""}
                onChange={(e) => setForm({ ...form, junior_lideres_equipa: e.target.value })}
              />
            </Field>
            <Field label="Ejecutivos en equipo">
              <Input
                type="number"
                value={form.lideres_executivos_equipa ?? ""}
                onChange={(e) => setForm({ ...form, lideres_executivos_equipa: e.target.value })}
              />
            </Field>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="cancel-edit">
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              className="bg-[#C57B67] hover:bg-[#B06A57] text-white"
              data-testid="save-edit"
            >
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[#736F68] font-medium">{label}</Label>
    {children}
  </div>
);

function Th({ col, sortCol, sortDir, onSort, right, children }) {
  const active = sortCol === col;
  return (
    <th
      className={`px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap hover:text-[#2D2A26] transition-colors ${right ? "text-right" : ""} ${active ? "text-[#C57B67]" : ""}`}
      onClick={() => onSort(col)}
    >
      <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>
        {children}
        {active
          ? (sortDir === "asc"
              ? <CaretUp size={11} weight="bold" />
              : <CaretDown size={11} weight="bold" />)
          : <CaretUpDown size={11} className="opacity-30" />}
      </span>
    </th>
  );
}